import { ProductDataProcessor, ProductRenderer, CONSTANTS as SEARCHSPRING_CONSTANTS } from '@theme/searchspring-core';
import { applyColorOverrides } from '@theme/search-results-renderer';

/**
 * @typedef {Object} SearchspringConfig
 * @property {string} productTypeToUse - Product type filter ("Made to Order" | "Ready to Ship" | "both")
 * @property {string} tags - Search tags for product recommendations
 * @property {string} productId - Specific product ID for recommendations
 * @property {number} limits - Maximum number of recommendations to fetch
 * @property {string} userId - User identifier for personalization
 * @property {string} lastViewedProductsIds - Comma-separated list of viewed product IDs
 *
 * @typedef {Object} SearchspringApiOptions
 * @property {string} [productId] - Specific product ID for recommendations
 * @property {number} [limits] - Maximum number of recommendations to fetch
 * @property {string} [userId] - User identifier for personalization
 * @property {string} [lastViewed] - Comma-separated list of viewed product IDs
 * @property {Record<string, string[]>} [bgfilter] - Background filters (e.g. { ss_color_code: ['AGV','FRV'] })
 *
 * @typedef {Object} SearchspringPriceRange
 * @property {string | number} size - Size identifier (* for all sizes)
 * @property {number} price - Current price
 * @property {number} compareAtPrice - Original price for comparison
 *
 * @typedef {Record<string, SearchspringPriceRange[]>} SearchspringColorPrices
 * @typedef {Record<string, SearchspringColorPrices>} SearchspringPrices
 *
 * @typedef {Object} ExtendedSearchspringColorOption
 * @property {string} key
 * @property {string} [color]
 * @property {string | null} [label]
 * @property {string[]} [images]
 *
 * @typedef {Object} ExtendedSearchspringProductOptions
 * @property {ExtendedSearchspringColorOption[]} [colors] - Available color options
 * @property {Record<string, number[]>} [sizes] - Size groups organized by color
 * @property {number[]} [allSizes] - All available sizes across colors

 * @typedef {Object} ExtendedSearchspringProduct
 * @property {string} [id] - Product ID
 * @property {string} [title] - Product title
 * @property {string} [handle] - Product handle
 * @property {any} [options] - Raw product options
 * @property {any} [prices] - Processed price data
 * @property {any} [parsedOptions] - Parsed options data
 * @property {ExtendedSearchspringProductOptions} [searchspringOptions] - Searchspring-specific options
 * @property {string} [mfield_searchspring_price] - Raw price metafield value
 */

// Constants
const CONSTANTS = {
  DEFAULT_LIMITS: 8,
  METAFIELD_NAMESPACE: 'searchspring',
  METAFIELD_KEYS: {
    COLORS: 'colors',
    COLOR_LABELS: 'color_labels',
    COLOR_SIZES: 'color_sizes',
    VARIANTS_GALLERY: 'variants_gallery',
    PRICE: 'price',
    PARENT_PRODUCT: 'parent_product',
    COLOR_PATTERN: 'color-pattern',
  },
  LOCAL_STORAGE_KEYS: {
    USER_ID: 'ssUserId',
    VIEWED_PRODUCTS: 'viewedProducts',
  },
};

class SearchspringApiService {
  /** @type {string} */
  siteId;

  /**
   * @param {string} siteId
   */
  constructor(siteId) {
    if (!siteId) {
      throw new Error('SearchspringApiService requires a siteId');
    }
    this.siteId = siteId;
  }

  /**
   * Fetch product recommendations from Searchspring API
   * @param {string} tags - Search tags for recommendations
   * @param {SearchspringApiOptions} [options] - Additional API options
   * @returns {Promise<SearchspringProduct[]>} Array of product data
   */
  async getRecommendations(tags, options = {}) {
    const {
      productId = '',
      limits = CONSTANTS.DEFAULT_LIMITS,
      userId = '',
      lastViewed = '',
      bgfilter = {},
    } = options;

    const productParam = productId ? `&product=${encodeURIComponent(productId)}` : '';

    // Build bgfilter query params (e.g. &bgfilter.ss_color_code=AGV&bgfilter.ss_color_code=FRV)
    let bgfilterParams = '';
    for (const [key, values] of Object.entries(bgfilter)) {
      for (const value of values) {
        bgfilterParams += `&bgfilter.${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
      }
    }

    const url = `https://${this.siteId}.a.searchspring.io/boost/${this.siteId}/recommend?tags=${encodeURIComponent(tags)}${productParam}&limits=${limits}&shopper=${encodeURIComponent(userId)}&lastViewed=${encodeURIComponent(lastViewed)}${bgfilterParams}`;

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }
      const data = await response.json();
      const flattened = data.flat();
      return (
        flattened[0]?.results?.map(
          /** @param {{ attributes: any }} product */
          (product) => product.attributes
        ) || []
      );
    } catch (error) {
      console.error('Searchspring API error:', error);
      throw error;
    }
  }
}

class SearchspringRecommendations extends HTMLElement {
  #config = {
    productTypeToUse: SEARCHSPRING_CONSTANTS.PRODUCT_TYPES.BOTH,
    tags: '',
    productId: '',
    currentStyle: '',
    limits: CONSTANTS.DEFAULT_LIMITS,
    userId: '',
    lastViewedProductsIds: '',
    promotedColors: '',
    promotedColorsMode: 'filter',
  };

  // add private site id field
  #siteId = '';

  /** @type {boolean} */
  #isB2B = false;

  /**
   * Lifecycle method called when element is added to DOM
   */
  async connectedCallback() {
    this.setSettings();

    if (!this.#config.tags) return;
    if (!this.#siteId) {
      console.warn('searchspring-recommendations: missing data-site-id attribute');
      return;
    }

    this.getRecommendations();
  }

  /**
   * Initialize settings from element attributes
   */
  setSettings() {
    this.#config.tags = this.getAttribute('data-tags') || '';
    this.#config.productTypeToUse =
      this.getAttribute('data-product-types') || SEARCHSPRING_CONSTANTS.PRODUCT_TYPES.BOTH;
    this.#config.productId = this.getAttribute('data-product') || '';
    this.#config.currentStyle = this.getAttribute('data-current-style') || '';
    this.#config.limits = Number(this.getAttribute('data-limits')) || CONSTANTS.DEFAULT_LIMITS;
    this.#config.userId = this.getUserId()?.value?.value || '';
    this.#config.lastViewedProductsIds = this.getLastViewedProducts(this.#config.currentStyle);
    // read site id from attribute only; no fallback to constants
    this.#siteId = this.getAttribute('data-site-id') || '';
    this.#config.promotedColors = this.getAttribute('data-promoted-colors') || '';
    this.#config.promotedColorsMode = this.getAttribute('data-promoted-colors-mode') || 'filter';
    this.#isB2B = this.getAttribute('data-is-b2b') === 'true';
  }

  /**
   * Get user ID from localStorage
   * @returns {{ value: { value: string } } | null} User ID data or null
   */
  getUserId() {
    try {
      const userIdData = localStorage.getItem(CONSTANTS.LOCAL_STORAGE_KEYS.USER_ID);
      return userIdData ? JSON.parse(userIdData) : null;
    } catch (error) {
      console.warn('Failed to parse user ID from localStorage:', error);
      return null;
    }
  }

  /**
   * Get last viewed products from localStorage
   * @param {string} [excludeStyle] - Style code to exclude (e.g. the current product)
   * @returns {string} Comma-separated product IDs
   */
  getLastViewedProducts(excludeStyle = '') {
    try {
      const lastViewedData = localStorage.getItem(CONSTANTS.LOCAL_STORAGE_KEYS.VIEWED_PRODUCTS);
      if (!lastViewedData || !lastViewedData.length) return '';
      /** @type {string[]} */
      const ids = JSON.parse(lastViewedData);
      const filtered = excludeStyle ? ids.filter((id) => id !== excludeStyle) : ids;
      return filtered.join(',');
    } catch (error) {
      console.warn('Failed to parse last viewed products from localStorage:', error);
      return '';
    }
  }

  /**
   * Fetch and render product recommendations
   */
  async getRecommendations() {
    try {
      const apiService = new SearchspringApiService(this.#siteId);

      /** @type {SearchspringApiOptions} */
      const apiOptions = {
        productId: this.#config.productId,
        limits: this.#config.limits,
        userId: this.#config.userId,
        lastViewed: this.#config.lastViewedProductsIds,
      };

      // Pass promoted colors as bgfilter to exclude non-matching products
      const promotedColorsList = this.#config.promotedColors
        ? this.#config.promotedColors.split(',').map((c) => c.trim()).filter(Boolean)
        : [];
      if (promotedColorsList.length > 0) {
        apiOptions.bgfilter = { 'ss_color_code': promotedColorsList };
      }

      const products = await apiService.getRecommendations(this.#config.tags, apiOptions);

      const productProcessor = new ProductDataProcessor();
      const formattedProducts = productProcessor.formatProducts(
        products,
        this.#config.productTypeToUse
      );
      if (this.#isB2B) {
        applyColorOverrides(formattedProducts, null, productProcessor, true);
      }
      const productsWithOptions = productProcessor.mapProductOptions(formattedProducts);

      if (productsWithOptions.length === 0) {
        this.style.display = 'none';
        return;
      }

      const renderer = new ProductRenderer(this);
      /** @type {{ promotedColors?: string[], promotedColorsMode?: string, isB2B?: boolean }} */
      const renderOptions = {};
      if (promotedColorsList.length > 0) {
        renderOptions.promotedColors = promotedColorsList;
        renderOptions.promotedColorsMode = this.#config.promotedColorsMode;
      }
      if (this.#isB2B) {
        renderOptions.isB2B = true;
      }
      await renderer.renderProducts(/** @type {SearchspringProduct[]} */ (productsWithOptions), true, false, renderOptions);
    } catch (error) {
      console.error('Failed to get recommendations:', error);
      this.style.display = 'none';
    }
  }

  /**
   * Lifecycle method called when element is removed from DOM
   */
  disconnectedCallback() {}
}

if (!customElements.get('searchspring-recommendations')) {
  customElements.define('searchspring-recommendations', SearchspringRecommendations);
}
